// The one spec both editors edit (`CfoSemanticQuerySpecV1`), plus the
// plain-language echo shown after every change. The echo mirrors
// `server/src/platform/views/summary.rs`; the server's summary on preview and
// save is authoritative.

export type Grain = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'transaction' | 'snapshot'
export type FilterOperator = 'eq' | 'not_eq' | 'in' | 'not_in' | 'between' | 'is_null' | 'is_not_null'

export type SemanticFilter = { dimension_id: string; operator: FilterOperator; member_ids: string[] }
export type DerivedMeasure = {
  measure_id: string
  label: string
  expression: Record<string, unknown>
  format_id: string | null
}
export type ViewSpec = {
  schema_version: 'fg.cfo_semantic_query.v1'
  company_key: string
  dataset_id: string
  dataset_version: number
  accounting_basis: 'accrual' | 'cash' | null
  currency: string | null
  grain: Grain
  period_window: { start: string; end: string; periods: number | null }
  dimensions: string[]
  measures: string[]
  derived_measures: DerivedMeasure[]
  filters: SemanticFilter[]
  pivot_dimensions: string[]
  sort: Array<{ field_id: string; descending: boolean }>
  top_n: number | null
  row_limit: number | null
  expected_source_set_sha256: string | null
}

export type PeriodBinding = { mode: 'fg_period'; trailing: number } | { mode: 'fixed' }

export type DatasetDef = {
  dataset_id: string
  dataset_version: number
  label: string
  description?: string
  supported_grains: Grain[]
  dimensions: string[]
  measures: string[]
  measure_definitions?: Array<{ id: string; deprecated?: boolean }>
}

export const LIMITS = {
  maxRows: 2_000_000,
  maxExcelTableRows: 1_000_000,
  maxPeriods: 240,
  maxFilterMembers: 250,
  maxDimensions: 24,
  maxMeasures: 50,
  maxTopN: 10_000,
} as const

const NAMED: Record<string, string> = {
  'period.month': 'Month',
  'period.quarter': 'Quarter',
  'period.year': 'Year',
}
const NAMED_LEAF: Record<string, string> = {
  account_label: 'Account',
  account_id: 'Account ID',
  statement_type: 'Statement',
  net: 'Net income',
  signed_net_amount: 'Net amount',
  customer: 'Customer',
  vendor: 'Vendor',
  class: 'Class',
}

/** Mirrors `catalog::field_label`. */
export function fieldLabel(id: string): string {
  if (NAMED[id]) return NAMED[id]
  const leaf = id.split('.').pop() || id
  if (NAMED_LEAF[leaf]) return NAMED_LEAF[leaf]
  const words = leaf.replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function memberLabel(member: string): string {
  if (member === 'profit_and_loss') return 'P&L'
  if (member === 'balance_sheet') return 'Balance sheet'
  if (member === 'cash_flow') return 'Cash flow'
  return member
}

export function periodDimension(dataset: DatasetDef | null | undefined, grain: Grain): string | null {
  const id =
    grain === 'month' ? 'period.month'
      : grain === 'quarter' ? 'period.quarter'
        : grain === 'year' ? 'period.year'
          : grain === 'snapshot' ? 'snapshot.date'
            : grain === 'day' ? 'transaction.date'
              : null
  return id && dataset?.dimensions.includes(id) ? id : null
}

export function activeMeasures(dataset: DatasetDef): string[] {
  const deprecated = new Set((dataset.measure_definitions || []).filter((m) => m.deprecated).map((m) => m.id))
  return dataset.measures.filter((id) => !deprecated.has(id))
}

const pad = (value: number) => String(value).padStart(2, '0')

export function monthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`
}

export function addMonths(month: string, delta: number): string {
  const [year, mon] = month.split('-').map(Number)
  const index = year * 12 + (mon - 1) + delta
  return `${Math.floor(index / 12)}-${pad((index % 12 + 12) % 12 + 1)}`
}

export function latestClosedMonth(today = new Date()): string {
  return addMonths(monthKey(today), -1)
}

function grainMonths(grain: Grain): number {
  return grain === 'quarter' ? 3 : grain === 'year' ? 12 : 1
}

export function periodCount(start: string, end: string, grain: Grain): number {
  const [sy, sm] = start.split('-').map(Number)
  const [ey, em] = end.split('-').map(Number)
  const months = (ey - sy) * 12 + em - sm + 1
  if (grain === 'quarter') return Math.floor((months + 2) / 3)
  if (grain === 'year') return ey - sy + 1
  return months
}

/** The window `trailing` periods of `grain` ending at `asOf` (mirrors `period::trailing_window`). */
export function trailingWindow(asOf: string, trailing: number, grain: Grain) {
  const end = asOf.slice(0, 7)
  const start = addMonths(end, -(Math.max(1, trailing) * grainMonths(grain) - 1))
  const periods = periodCount(start, end, grain)
  return { start, end, periods: periods <= LIMITS.maxPeriods ? periods : null }
}

export function emptySpec(dataset: DatasetDef, companyKey: string, asOf: string): { spec: ViewSpec; binding: PeriodBinding } {
  const grain = dataset.supported_grains[0] || 'month'
  const period = periodDimension(dataset, grain)
  const measures = activeMeasures(dataset)
  return {
    spec: {
      schema_version: 'fg.cfo_semantic_query.v1',
      company_key: companyKey,
      dataset_id: dataset.dataset_id,
      dataset_version: dataset.dataset_version,
      accounting_basis: 'accrual',
      currency: null,
      grain,
      period_window: trailingWindow(asOf, 12, grain),
      dimensions: period ? [period] : [],
      measures: measures.slice(0, 1),
      derived_measures: [],
      filters: [],
      pivot_dimensions: [],
      sort: [],
      top_n: null,
      row_limit: null,
      expected_source_set_sha256: null,
    },
    binding: { mode: 'fg_period', trailing: 12 },
  }
}

/** Client-side check matching `spec::validate_view_spec`; returns problems in plain words. */
export function specProblems(spec: ViewSpec, dataset: DatasetDef | null): string[] {
  const problems: string[] = []
  if (!dataset) return ['Choose a dataset.']
  if (!spec.measures.length && !spec.derived_measures.length) problems.push('Pick at least one measure.')
  if (!spec.dimensions.length && !spec.pivot_dimensions.length) problems.push('Pick at least one dimension.')
  if (spec.dimensions.length > LIMITS.maxDimensions) problems.push(`At most ${LIMITS.maxDimensions} dimensions.`)
  if (spec.measures.length > LIMITS.maxMeasures) problems.push(`At most ${LIMITS.maxMeasures} measures.`)
  if (spec.period_window.start > spec.period_window.end) problems.push('The period starts after it ends.')
  else if (periodCount(spec.period_window.start, spec.period_window.end, spec.grain) > LIMITS.maxPeriods) problems.push(`At most ${LIMITS.maxPeriods} periods.`)
  const members = spec.filters.reduce((sum, filter) => sum + filter.member_ids.length, 0)
  if (members > LIMITS.maxFilterMembers) problems.push(`At most ${LIMITS.maxFilterMembers} filter members in total.`)
  if (spec.top_n !== null && !spec.sort.length) problems.push('Top-N needs a sort.')
  if (spec.top_n !== null && (spec.top_n < 1 || spec.top_n > LIMITS.maxTopN)) problems.push(`Top-N is between 1 and ${LIMITS.maxTopN}.`)
  for (const sort of spec.sort) {
    if (![...spec.dimensions, ...spec.pivot_dimensions, ...spec.measures].includes(sort.field_id)) problems.push(`Sort by a field in the view (${fieldLabel(sort.field_id)} is not).`)
  }
  return problems
}

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] || ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function monthWords(month: string): string {
  const [year, mon] = month.split('-').map(Number)
  return `${MONTH_NAMES[mon - 1]} ${year}`
}

function unit(grain: Grain, count: number): string {
  const base = grain === 'quarter' ? 'quarter' : grain === 'year' ? 'year' : 'month'
  return count === 1 ? base : `${base}s`
}

/** Mirrors `summary::summarize`. */
export function summarizeSpec(spec: ViewSpec, binding: PeriodBinding, dataset: DatasetDef | null): string {
  const measures = [...spec.measures.map(fieldLabel), ...spec.derived_measures.map((d) => d.label)]
  const dims = spec.dimensions.map(fieldLabel)
  let out = dims.length ? `${joinAnd(measures)} by ${joinAnd(dims)}` : joinAnd(measures)
  if (spec.pivot_dimensions.length) out += `, pivot ${joinAnd(spec.pivot_dimensions.map(fieldLabel))}`
  const { start, end } = spec.period_window
  if (binding.mode === 'fg_period') out += `, last ${binding.trailing} ${unit(spec.grain, binding.trailing)} (ending ${monthWords(end)}, follows FG_Period)`
  else if (start === end) out += `, in ${monthWords(start)}`
  else out += `, from ${monthWords(start)} to ${monthWords(end)}`
  for (const filter of spec.filters) {
    const members = joinAnd(filter.member_ids.map(memberLabel))
    const dim = fieldLabel(filter.dimension_id)
    if (filter.operator === 'eq' || filter.operator === 'in') out += `, only ${dim} ${members}`
    else if (filter.operator === 'not_eq' || filter.operator === 'not_in') out += `, excluding ${dim} ${members}`
    else if (filter.operator === 'between') out += `, ${dim} between ${filter.member_ids.join(' and ')}`
    else if (filter.operator === 'is_null') out += `, where ${dim} is blank`
    else out += `, where ${dim} is set`
  }
  const sort = spec.sort[0]
  if (sort && spec.top_n !== null) out += `, ${sort.descending ? 'top' : 'bottom'} ${spec.top_n} by ${fieldLabel(sort.field_id)}`
  else if (sort) out += `, sorted by ${fieldLabel(sort.field_id)} ${sort.descending ? 'largest first' : 'smallest first'}`
  if (dataset) out += ` — ${dataset.label.replace(/^Governed /, '')}.`
  return out.charAt(0).toUpperCase() + out.slice(1)
}

/** One line naming what a manual edit changed. */
export function describeChange(before: ViewSpec, after: ViewSpec): string | null {
  const added = (a: string[], b: string[]) => b.filter((x) => !a.includes(x))
  const removed = (a: string[], b: string[]) => a.filter((x) => !b.includes(x))
  const parts: string[] = []
  if (before.dataset_id !== after.dataset_id) parts.push(`Switched to ${fieldLabel(after.dataset_id)}`)
  if (before.grain !== after.grain) parts.push(`Grain is now ${after.grain}`)
  for (const id of added(before.measures, after.measures)) parts.push(`Added ${fieldLabel(id)}`)
  for (const id of removed(before.measures, after.measures)) parts.push(`Removed ${fieldLabel(id)}`)
  for (const id of added(before.dimensions, after.dimensions)) parts.push(`Added ${fieldLabel(id)} as a dimension`)
  for (const id of removed(before.dimensions, after.dimensions)) parts.push(`Removed the ${fieldLabel(id)} dimension`)
  for (const id of added(before.pivot_dimensions, after.pivot_dimensions)) parts.push(`Pivoted ${fieldLabel(id)} into columns`)
  if (JSON.stringify(before.filters) !== JSON.stringify(after.filters)) parts.push('Changed the filters')
  if (JSON.stringify(before.period_window) !== JSON.stringify(after.period_window)) parts.push(`Period is ${monthWords(after.period_window.start)} to ${monthWords(after.period_window.end)}`)
  if (JSON.stringify(before.sort) !== JSON.stringify(after.sort) || before.top_n !== after.top_n) parts.push('Changed the sort')
  return parts.length ? `${parts.join('; ')}.` : null
}

/** Mirrors `spec::check_excel_destination` and `view-link.js`. */
export function excelDestinations(rowCount: number | null | undefined) {
  if (rowCount === null || rowCount === undefined) return { table: false, formulas: false, reason: 'Preview the view to see its size.' }
  if (rowCount > LIMITS.maxRows) return { table: false, formulas: false, reason: 'Over the 2,000,000-row limit.' }
  if (rowCount > LIMITS.maxExcelTableRows) return { table: false, formulas: true, reason: `${rowCount.toLocaleString('en-US')} rows is more than an Excel table holds; link it as FUSEGRID formulas.` }
  return { table: true, formulas: true, reason: null }
}
